<template>
	<Modal
		v-model:visible="visible"
		:title="modalTitle"
		:destroyOnClose="true"
		:closable="!isSubmitting"
		:keyboard="!isSubmitting"
		:maskClosable="!isSubmitting"
		width="500px"
		@ok="onOk"
		@cancel="onCancel"
	>
		<Form
			:model="formState"
			:label-col="{ span: 10 }"
			:wrapper-col="{ span: 14 }"
			:colon="false"
		>
			<FormItem
				:label="$t('settings.vrsSettingsForm.lsFile.dateLabel')"
			>
				<Space align="start">
					<FormItem v-bind="validateInfos.year" no-style>
						<InputDecimal
							v-model="formState.year"
							:allowPoint="false"
							:placeholder="$t('settings.vrsSettingsForm.lsFile.yearPlaceholder')"
						/>
					</FormItem>
					<FormItem v-bind="validateInfos.monthDay" no-style>
						<Select
							v-model:value="formState.monthDay"
							:options="monthDayOptions"
							:placeholder="$t('settings.vrsSettingsForm.lsFile.monthDayPlaceholder')"
							class="month-day-select"
						/>
					</FormItem>
				</Space>
			</FormItem>
			<FormItem
				:label="$t('settings.vrsSettingsForm.lsFile.valueLabel')"
				v-bind="validateInfos.value"
			>
				<InputDecimal v-model="formState.value" :allowPoint="false" />
			</FormItem>
		</Form>
		<template #footer>
			<Button key="back" @click="onCancel" :loading="isSubmitting">{{ $t('settings.vrsSettingsForm.cancelButtonLabel') }}</Button>
			<Button type="primary" @click="onOk" :loading="isSubmitting">{{ $t('settings.vrsSettingsForm.buttonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, computed, watch, toRef, toRaw } from 'vue';
import { Modal, Form, FormItem, Button, Select, Space, message } from 'ant-design-vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Validators from '@/api/Validators';
import Utils from '@/api/Utils';

import { useI18n } from 'vue-i18n';

import { showErrorMessage } from '@/components/common/errorMessage';
import InputDecimal from '@/components/common/InputDecimal';

const getDefaultState = () => ({
	id: null,
	year: null,
	monthDay: null,
	value: 0,
});

const parseLeapSecondDate = dateStr => {
	if (!dateStr) {
		return { year: null, monthDay: null };
	}
	const parts = dateStr.split('-');
	return {
		year: Number(parts[0]),
		monthDay: parts[1] + '-' + parts[2],
	};
};

const formatLeapSecondDate = (year, monthDay) => {
	if (year == null || !monthDay) {
		return null;
	}
	return String(year) + '-' + monthDay;
};

export default {
	components: {
		Modal, Form, FormItem, Button, Select, Space, InputDecimal,
	},
	emits: ['update:modelValue'],
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		onLeapSecondsUpdate: {
			type: Function,
			default: null,
		},
		item: {
			type: Object,
			default: null,
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const initialFormState = reactive(getDefaultState());
		const formState = reactive(getDefaultState());
		const { validate, validateInfos } = Validators.useLeapSecondsFormRules(formState);
		const isSubmitting = ref(false);
		const monthDayOptions = Consts.leapSecondsMonthDayOptions;

		const itemRef = toRef(props, 'item');
		const modalTitle = computed(() => {
			if (itemRef.value == null) {
				return t('settings.vrsSettingsForm.lsFile.modalCreateTitle');
			}
			return t('settings.vrsSettingsForm.lsFile.modalEditTitle');
		});

		const resetFormFromItem = value => {
			if (value) {
				const dateParts = parseLeapSecondDate(value.date);
				Utils.updateObject(initialFormState, {
					id: value.id,
					year: dateParts.year,
					monthDay: dateParts.monthDay,
					value: value.value,
				});
			} else {
				Utils.updateObject(initialFormState, getDefaultState());
			}
			Utils.updateObject(formState, initialFormState);
		};

		watch(itemRef, resetFormFromItem, { immediate: true });

		const modelValueRef = toRef(props, 'modelValue');
		const visible = computed({
			get: () => modelValueRef.value,
			set: value => emit('update:modelValue', value),
		});
		watch(modelValueRef, value => {
			if (value) {
				resetFormFromItem(itemRef.value);
			}
		});

		const handleResponse = response => {
			if (response.status === 'ok') {
				message.success(t('settings.vrsSettingsForm.messages.success'));
				visible.value = false;
				if (props.onLeapSecondsUpdate) {
					props.onLeapSecondsUpdate();
				}
			} else {
				showErrorMessage(response.errors);
			}
			isSubmitting.value = false;
		};

		const onOk = () => {
			validate().then(() => {
				isSubmitting.value = true;
				const rawState = toRaw(formState);
				const payload = {
					...rawState,
					date: formatLeapSecondDate(rawState.year, rawState.monthDay),
				};
				delete payload.year;
				delete payload.monthDay;
				if (payload.id) {
					Api.vrsLeapSecondsUpdate(payload, handleResponse);
				} else {
					Api.vrsLeapSecondsPost(payload, handleResponse);
				}
			}).catch(err => {
				console.log('error', err);
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			formState,
			validateInfos,
			isSubmitting,
			visible,
			modalTitle,
			monthDayOptions,
			onOk,
			onCancel,
		};
	},
}
</script>

<style scoped>
.month-day-select {
	min-width: 96px;
}
</style>
